import {
    BILLING_ENTITLEMENT_STATE_SOURCE,
    BillingEntitlementStateService,
} from '@services/billing-entitlement-state.service'
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs'
import {
    AfterViewInit,
    DestroyRef,
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    inject,
} from '@angular/core'
import { EntitlementName } from '@app/domain/Entitlement'
import { NgControl } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

type DisableableElement = HTMLElement & { disabled: boolean }
type EntitlementDisabledInput = EntitlementName | '' | null

@Directive({
    selector: '[appBillingEntitlementDisabled]',
    standalone: true,
})
export class BillingEntitlementDisabledDirective implements AfterViewInit, OnInit, OnDestroy {

    private entitlementStateService = inject(BillingEntitlementStateService)
    private inheritedStateSource = inject(BILLING_ENTITLEMENT_STATE_SOURCE, { optional: true })
    private destroyRef = inject(DestroyRef)
    private elementRef = inject(ElementRef<DisableableElement>)
    private renderer = inject(Renderer2)
    private ngControl = inject(NgControl, { optional: true, self: true })
    private entitlementName$ = new BehaviorSubject<EntitlementDisabledInput>(null)
    private entitlementUsage$ = new BehaviorSubject<number | null>(null)
    private usesQuantityEntitlement = false
    private viewInitialized = false
    private shouldDisable = false
    private disabledOutsideEntitlement = false
    private ariaDisabledOutsideEntitlement: string | null = null

    @Input() set appBillingEntitlementDisabled(
        entitlementName: EntitlementName | '' | null | undefined
    ) {
        const name = entitlementName ?? null
        this.setEntitlementDisabled(name !== null)
        this.entitlementName$.next(name)
    }

    @Input() set appBillingEntitlementDisabledUsage(currentUsage: number | null | undefined) {
        this.usesQuantityEntitlement = true
        const normalizedUsage = typeof currentUsage === 'number'
            && Number.isInteger(currentUsage)
            && currentUsage >= 0
            ? currentUsage
            : null
        this.entitlementUsage$.next(normalizedUsage)
    }

    ngOnInit() {
        combineLatest([
            this.entitlementName$,
            this.entitlementUsage$,
        ])
            .pipe(
                distinctUntilChanged((previous, current) => {
                    return previous[0] === current[0] && previous[1] === current[1]
                }),
                switchMap(([entitlementName, currentUsage]) => {
                    if (entitlementName === '') {
                        if (this.inheritedStateSource === null) {
                            throw new Error(
                                'appBillingEntitlementDisabled requires an entitlement or a parent appBillingEntitlement'
                            )
                        }
                        return this.inheritedStateSource.billingEntitlementState$
                    }
                    return this.usesQuantityEntitlement
                        ? this.entitlementStateService.getState$(entitlementName, currentUsage)
                        : this.entitlementStateService.getState$(entitlementName)
                }),
                map(state => state !== 'unlocked'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(shouldDisable => this.setEntitlementDisabled(shouldDisable))

        this.ngControl?.statusChanges
            ?.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.applyDisabledState())
    }

    ngAfterViewInit() {
        const host = this.elementRef.nativeElement
        if (!('disabled' in host)) {
            throw new Error('appBillingEntitlementDisabled requires an element with a disabled property')
        }
        this.captureDisabledStateOutsideEntitlement()
        this.viewInitialized = true
        this.applyDisabledState()
    }

    ngOnDestroy() {
        if (!this.viewInitialized) {
            return
        }
        this.restoreDisabledStateOutsideEntitlement()
    }

    private setEntitlementDisabled(shouldDisable: boolean) {
        if (this.viewInitialized && shouldDisable && !this.shouldDisable) {
            this.captureDisabledStateOutsideEntitlement()
        }
        this.shouldDisable = shouldDisable
        this.applyDisabledState()
    }

    private applyDisabledState() {
        if (!this.viewInitialized) {
            return
        }
        const disabledOutsideEntitlement = this.ngControl?.disabled === true
            || this.disabledOutsideEntitlement
        this.setDisabled(this.shouldDisable || disabledOutsideEntitlement)
        if (!this.shouldDisable) {
            this.restoreAriaDisabledStateOutsideEntitlement()
        }
    }

    private captureDisabledStateOutsideEntitlement() {
        const host = this.elementRef.nativeElement
        this.disabledOutsideEntitlement = this.ngControl === null && host.disabled
        this.ariaDisabledOutsideEntitlement = host.getAttribute('aria-disabled')
    }

    private restoreDisabledStateOutsideEntitlement() {
        const disabled = this.ngControl?.disabled === true || this.disabledOutsideEntitlement
        this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', disabled)
        this.restoreAriaDisabledStateOutsideEntitlement()
    }

    private restoreAriaDisabledStateOutsideEntitlement() {
        const host = this.elementRef.nativeElement
        if (this.ariaDisabledOutsideEntitlement === null) {
            this.renderer.removeAttribute(host, 'aria-disabled')
        } else {
            this.renderer.setAttribute(host, 'aria-disabled', this.ariaDisabledOutsideEntitlement)
        }
    }

    private setDisabled(disabled: boolean) {
        const host = this.elementRef.nativeElement
        this.renderer.setProperty(host, 'disabled', disabled)
        if (disabled) {
            this.renderer.setAttribute(host, 'aria-disabled', 'true')
        }
    }
}
